import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import {
  GenerateLinkResponseSchema,
  ResumeSessionResponseSchema,
  GetInteractionsResponseSchema,
  StopListenerResponseSchema,
  DeleteSessionResponseSchema,
  ListSessionsResponseSchema,
  SetDynamicResponseResponseSchema,
  DynamicResponseConfigSchema,
  ErrorResponseSchema,
  SafeSessionIdSchema,
} from './interact.schema';
import { z } from 'zod';

export const INTERACT_PATH_NAME = 'api/interact';

export const InteractContract = terraContract
  .prefix(`/${INTERACT_PATH_NAME}`)
  .router({
    generateLink: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/generate',
        summary:
          'Generate a new Terra Link (interactsh domain) for OOB testing',
        inputStructure: 'detailed',
      })
      .output(GenerateLinkResponseSchema)
      .errors({
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    resumeSession: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/{sessionId}/resume',
        summary: 'Resume a stopped session',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            sessionId: SafeSessionIdSchema,
          }),
        }),
      )
      .output(ResumeSessionResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    listSessions: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/sessions',
        summary: 'List all active sessions for the current tenant',
        inputStructure: 'detailed',
      })
      .output(ListSessionsResponseSchema)
      .errors({
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    getInteractions: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/{sessionId}/interactions',
        summary: 'Get all interactions for a session',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            sessionId: SafeSessionIdSchema,
          }),
        }),
      )
      .output(GetInteractionsResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    stopListener: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/{sessionId}/stop',
        summary: 'Stop the background listener for a session',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            sessionId: SafeSessionIdSchema,
          }),
        }),
      )
      .output(StopListenerResponseSchema)
      .errors({
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    setDynamicResponse: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/{sessionId}/dynamic-response',
        summary: 'Configure a dynamic HTTP response for the OOB domain',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            sessionId: SafeSessionIdSchema,
          }),
          body: DynamicResponseConfigSchema,
        }),
      )
      .output(SetDynamicResponseResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    deleteSession: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{sessionId}',
        summary: 'Delete a session and all its data from Redis',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            sessionId: SafeSessionIdSchema,
          }),
        }),
      )
      .output(DeleteSessionResponseSchema)
      .errors({
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
  });
